<template>
  <div class="form-group">
    <div v-if="dayTimeMode" class="form-group form--inline">
      <n-select v-model="selectedDay" label="When" required>
        <option value="today">Today</option>
        <option value="tomorrow">Tomorrow</option>
      </n-select>
      <n-select v-model="selectedDate" :label="hourLabel" required>
        <option disabled :value="null">{{ hourPlaceholder }}</option>
        <option v-for="option in dayTimeOptions" :value="option.date" :key="option.date.toLocaleString()">{{ option.label }}</option>
      </n-select>

    </div>
    <div v-else-if="useNativeTimeInput" class="form-group">
      <n-input v-model="nativeTimeInput" v-bind="$attrs" ref="nativeInput" type="time" :label="nativeInputLabel"
               :min="nativeInputMinTime" max="23:00" required/>
    </div>
    <div v-else class="form-group form--inline">
      <n-select v-model="selectedHour" :label="hourLabel" required>
        <option disabled :value="null">{{ hourPlaceholder }}</option>
        <option v-for="hour in hourSelectOptions" :value="hour" :key="hour">{{ hour }}</option>
      </n-select>
      <n-select v-model="selectedMinute" :disabled="selectedHour == null" :label="minuteLabel" required>
        <option disabled :value="null">{{ minutePlaceholder }}</option>
        <option v-for="minute in minuteSelectOptions" :value="minute" :key="minute">{{ minute | padStart('00') }}
        </option>
      </n-select>
    </div>
  </div>
</template>

<script>

import padStart from '../../filters/pad-start'
import {nextTick} from 'nandos-timing-utils'

export default {

  name: 'NTimePicker',

  props: {
    value: {type: Date},
    useNativeTimeInput: {type: Boolean, default: true},
    minTime: {type: Date, default: getTodayStart},
    maxTime: {type: Date, default: getTodayEnd},
    minuteIncrements: {type: Number, default: 15},
    dailyMinHours: {type: Number, default: 0},
    dailyMaxHours: {type: Number, default: 23},
    hourLabel: {type: String, default: 'Hour'},
    hourPlaceholder: {type: String, default: 'Select Hour'},
    minuteLabel: {type: String, default: 'Minute'},
    minutePlaceholder: {type: String, default: 'Select minute'},
    nativeInputLabel: {type: String, default: 'Time'},
    dayTimeMode: {type: Boolean}
  },

  data() {
    return {
      nativeTimeInput: '',
      selectedHour: null,
      selectedMinute: null,
      selectedDay: 'today',
      selectedDate: null,
    }
  },

  computed: {

    selectedTime: {
      get() {
        if (this.selectedHour != null && this.selectedMinute != null) {
          let date = getTodayStart()
          date.setHours(this.selectedHour)
          date.setMinutes(this.selectedMinute)
          return date
        }
      },

      set(date) {
        if (date != null) {
          this.selectedHour = date.getHours()
          this.selectedMinute = date.getMinutes()
          let hour = padStart(this.selectedHour, '00')
          let minute = padStart(this.selectedMinute, '00')
          this.nativeTimeInput = `${hour}:${minute}`
        } else {
          this.selectedHour = this.selectedMinute = null
          this.nativeInput = ''
        }
      },
    },

    relativeMinDate() {
      return new Date(this.selectedDay === 'today' ? this.minTime : new Date(this.maxTime.getFullYear(), this.maxTime.getMonth(), this.maxTime.getDate(), this.dailyMinHours, 0, 0))
    },
    relativeMaxDate() {
      return new Date(this.selectedDay === 'tomorrow' ? this.maxTime : new Date(this.minTime.getFullYear(), this.minTime.getMonth(), this.minTime.getDate(), this.dailyMaxHours, 0, 0, 0))
    },

    dayTimeOptions() {
      let date = new Date(this.relativeMinDate);
      let options = []
      let twentyForHoursFromNow = new Date(Date.now() + 60 * 60 * 24 * 1000);
      while (date.getTime() <= this.relativeMaxDate.getTime() && date.getTime() < twentyForHoursFromNow) {
        options.push({
          date,
          label: `${padStart(date.getHours(), '00')}:${padStart(date.getMinutes(), '00')}`
        });
        date = new Date(date.getTime() + this.minuteIncrements * 60 * 1000)
      }
      return options
    },

    hourSelectOptions() {
      let hour = this.minTime.getHours()

      let options = []
      while (hour < 24) {
        options.push(hour)
        hour++
      }
      return options
    },

    minuteSelectOptions() {
      let minute = this.minTime.getMinutes()

      // If the selected hour is greater than the current(now) hour, allow the full range of minutes (from 0)
      if (this.selectedHour > this.minTime.getHours()) {
        minute = 0
      }


      let options = []
      while (minute < 60) {
        options.push(minute)
        minute += this.minuteIncrements
      }
      return options
    },

    nativeInputMinTime() {
      let hour = this.minTime.getHours()
      let minute = padStart(this.minTime.getMinutes(), '00')
      return `${hour}:${minute}`
    },
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        this.selectedTime = this.value
      }
    },

    selectedTime() {
      // If the user has made a valid date selection (hour & minute)
      let isValidDate = this.selectedTime instanceof Date && isFinite(this.selectedTime)
      nextTick(() => this.$emit('input', isValidDate ? this.selectedTime : undefined))
    },

    selectedDay() {
      this.selectedDate = null;
    },

    selectedDate(){
      // If the user has made a valid date selection (hour & minute)
      let isValidDate = this.selectedDate instanceof Date && isFinite(this.selectedDate)
      nextTick(() => this.$emit('input', isValidDate ? this.selectedDate : undefined))
    },

    selectedHour() {
      if (!this.useNativeTimeInput) {

        // If the selected minute option is no longer available clear the selection
        if (this.minuteSelectOptions.includes(this.selectedMinute) == false)
          this.selectedMinute = null

        // If there is only 1 minute option, automatically select it
        if (this.minuteSelectOptions.length == 1)
          this.selectedMinute = this.minuteSelectOptions[0]
      }
    },

    nativeTimeInput(val) {
      nextTick(() => {
        let timeParts = val.split(':')
        this.selectedHour = parseInt(timeParts[0])
        this.selectedMinute = parseInt(timeParts[1])
      })
    },
  },

  mounted() {
    if(Date.now() >= this.relativeMaxDate.getTime()){
      this.selectedDay = 'tomorrow'
    }
  }
}

function getTodayStart() {
  let d = new Date()
  d.setHours(this.dailyMinHours)
  d.setMinutes(0)
  d.setSeconds(0)
  d.setMilliseconds(0)
  return d
}

function getTodayEnd() {
  let d = new Date()
  d.setHours(this.dailyMaxHours)
  d.setMinutes(0)
  d.setSeconds(0)
  d.setMilliseconds(0)
  return d
}
</script>